/**
 * Croonborg, New Supreme System (1917), p. 100: "Table of Proportionate
 * Measures from Height and Circumference". For each breast measure (32–48 in)
 * and height (5 ft 4 in to 6 ft) it gives the short measures a cutter would
 * otherwise take by hand: depth of scye, strap, over-shoulder and length of
 * sleeve. Blade, front measure and width of back depend on the breast alone,
 * and the waist is printed for breasts up to 42 only.
 *
 * Values are in inches, as printed. Two cells break the table's own run and
 * are corrected here (see `supremeProportionMisprints`). No spec reads the
 * table yet: it is shared data for a future proportionate-measures mode (see
 * reference/books/croonborg-supreme-1917/foundations.md).
 */

/** Heights the table's columns are for, in inches (5 ft 4 in to 6 ft). */
export const supremeProportionHeights = [64, 65, 66, 67, 68, 69, 70, 71, 72] as const;

export interface SupremeProportionRow {
  /** Breast measure, inches (the whole measure, not the divisions). */
  breast: number;
  /** One value per height in `supremeProportionHeights`. */
  depthOfScye: number[];
  strap: number[];
  overShoulder: number[];
  sleeveLength: number[];
  blade: number;
  frontMeasure: number;
  widthOfBack: number;
  /** Proportionate waist; the book leaves it blank above 42 breast. */
  waist?: number;
}

export interface SupremeProportionateMeasures {
  depthOfScye: number;
  strap: number;
  overShoulder: number;
  sleeveLength: number;
  blade: number;
  frontMeasure: number;
  widthOfBack: number;
  waist?: number;
}

const fractions: Record<string, number> = { "⅛": 1 / 8, "¼": 2 / 8, "⅜": 3 / 8, "½": 4 / 8, "⅝": 5 / 8, "¾": 6 / 8, "⅞": 7 / 8 };

/** "10⅝" → 10.625. */
function inches(text: string): number {
  const match = /^(\d+)([⅛¼⅜½⅝¾⅞]?)$/.exec(text);
  if (!match) throw new Error(`Bad table value: ${text}`);
  return Number(match[1]) + (match[2] ? fractions[match[2]] : 0);
}

const run = (text: string) => text.trim().split(/\s+/).map(inches);

function row(
  breast: number,
  depthOfScye: string,
  strap: string,
  overShoulder: string,
  sleeveLength: string,
  [blade, frontMeasure, widthOfBack, waist]: [string, string, string, string?],
): SupremeProportionRow {
  return {
    breast,
    depthOfScye: run(depthOfScye),
    strap: run(strap),
    overShoulder: run(overShoulder),
    sleeveLength: run(sleeveLength),
    blade: inches(blade),
    frontMeasure: inches(frontMeasure),
    widthOfBack: inches(widthOfBack),
    ...(waist ? { waist: inches(waist) } : {}),
  };
}

// Columns: 5-4, 5-5, 5-6, 5-7, 5-8, 5-9, 5-10, 5-11, 6 ft.
export const supremeProportionTable: SupremeProportionRow[] = [
  row(32,
    "7¾ 7⅞ 8 8⅛ 8¼ 8⅜ 8½ 8⅝ 8¾",
    "10½ 10⅝ 10¾ 10⅞ 11 11⅛ 11¼ 11⅜ 11½",
    "15 15⅛ 15⅜ 15½ 15⅝ 15⅞ 16 16⅛ 16⅜",
    "18 18¼ 18½ 18¾ 19 19¼ 19½ 19¾ 20",
    ["10", "6", "6¼", "27"]),
  row(33,
    "8 8⅛ 8¼ 8⅜ 8½ 8⅝ 8¾ 8⅞ 9",
    "10¾ 10⅞ 11 11⅛ 11¼ 11⅜ 11½ 11⅝ 11¾",
    "15⅜ 15½ 15⅝ 15⅞ 16 16⅛ 16⅜ 16½ 16⅝",
    "17¾ 18 18¼ 18½ 18¾ 19 19¼ 19½ 19¾",
    ["10¼", "6¼", "6⅜", "28"]),
  row(34,
    "8⅛ 8¼ 8⅜ 8½ 8⅝ 8¾ 8⅞ 9 9⅛",
    "11 11⅛ 11¼ 11⅜ 11½ 11⅝ 11¾ 11⅞ 12",
    "15⅝ 15¾ 16 16⅛ 16⅜ 16½ 16¾ 16⅞ 17⅛",
    "17⅝ 17⅞ 18⅛ 18⅜ 18⅝ 18⅞ 19⅛ 19⅜ 19⅝",
    ["10½", "6½", "6⅝", "29"]),
  row(35,
    "8⅜ 8½ 8⅝ 8¾ 8⅞ 9 9⅛ 9¼ 9⅜",
    "11¼ 11⅜ 11½ 11⅝ 11¾ 11⅞ 12 12⅛ 12¼",
    "15⅞ 16 16¼ 16⅜ 16⅝ 16¾ 17 17⅛ 17⅜",
    "17⅜ 17⅝ 17⅞ 18⅛ 18⅜ 18⅝ 18⅞ 19⅛ 19⅜",
    ["10¾", "6¾", "6¾", "30"]),
  row(36,
    "8½ 8⅝ 8¾ 8⅞ 9 9⅛ 9¼ 9⅜ 9½",
    "11½ 11⅝ 11¾ 11⅞ 12 12⅛ 12¼ 12⅜ 12½",
    "16¼ 16⅜ 16⅝ 16¾ 17 17⅛ 17⅜ 17½ 17⅝",
    "17¼ 17½ 17¾ 18 18¼ 18½ 18¾ 19 19¼",
    ["11", "7", "7", "31"]),
  row(37,
    "8⅝ 8¾ 8⅞ 9 9⅛ 9¼ 9⅜ 9½ 9⅝",
    "11¾ 11⅞ 12 12⅛ 12¼ 12⅜ 12½ 12⅝ 12¾",
    "16⅝ 16¾ 17 17⅛ 17⅜ 17½ 17¾ 17⅞ 18⅛",
    "17 17¼ 17½ 17¾ 18 18¼ 18½ 18¾ 19",
    ["11¼", "7¼", "7⅛", "32"]),
  row(38,
    "8⅞ 9 9⅛ 9¼ 9⅜ 9½ 9⅝ 9¾ 9⅞",
    "12 12⅛ 12¼ 12⅜ 12½ 12⅝ 12¾ 12⅞ 13",
    "16⅞ 17 17¼ 17⅜ 17⅝ 17¾ 18 18⅛ 18⅜",
    "16⅞ 17⅛ 17⅜ 17⅝ 17⅞ 18⅛ 18⅜ 18⅝ 18⅞",
    ["11½", "7½", "7⅜", "33"]),
  row(39,
    "9 9⅛ 9¼ 9⅜ 9½ 9⅝ 9¾ 9⅞ 10",
    "12¼ 12⅜ 12½ 12⅝ 12¾ 12⅞ 13 13⅛ 13¼",
    "17¼ 17⅜ 17⅝ 17¾ 18 18⅛ 18⅜ 18½ 18¾",
    "16⅝ 16⅞ 17⅛ 17⅜ 17⅝ 17⅞ 18⅛ 18⅜ 18⅝",
    ["11¾", "7¾", "7½", "34"]),
  row(40,
    "9¼ 9⅜ 9½ 9⅝ 9¾ 9⅞ 10 10⅛ 10¼",
    "12½ 12⅝ 12¾ 12⅞ 13 13⅛ 13¼ 13⅜ 13½",
    "17⅝ 17¾ 18 18⅛ 18⅜ 18½ 18¾ 18⅞ 19⅛",
    "16½ 16¾ 17 17¼ 17½ 17¾ 18 18¼ 18½",
    ["12", "8", "7¾", "35"]),
  row(41,
    "9⅜ 9½ 9⅝ 9¾ 9⅞ 10 10⅛ 10¼ 10⅜",
    "12¾ 12⅞ 13 13⅛ 13¼ 13⅜ 13½ 13⅝ 13¾",
    "17⅞ 18 18¼ 18⅜ 18⅝ 18¾ 19 19⅛ 19⅜",
    // The book prints 18⅞ at 6 ft; the run (¼ in per inch of height) gives 18¼.
    "16¼ 16½ 16¾ 17 17¼ 17½ 17¾ 18 18¼",
    ["12¼", "8¼", "7⅞", "36"]),
  row(42,
    "9⅝ 9¾ 9⅞ 10 10⅛ 10¼ 10⅜ 10½ 10⅝",
    "13 13⅛ 13¼ 13⅜ 13½ 13⅝ 13¾ 13⅞ 14",
    "18¼ 18⅜ 18⅝ 18¾ 19 19⅛ 19⅜ 19½ 19¾",
    "16⅛ 16⅜ 16⅝ 16⅞ 17⅛ 17⅜ 17⅝ 17⅞ 18⅛",
    ["12½", "8½", "8⅛", "37"]),
  row(43,
    "9¾ 9⅞ 10 10⅛ 10¼ 10⅜ 10½ 10⅝ 10¾",
    "13⅛ 13¼ 13⅜ 13½ 13⅝ 13¾ 13⅞ 14 14⅛",
    "18½ 18⅝ 18⅞ 19 19¼ 19⅜ 19⅝ 19¾ 19⅞",
    "15⅞ 16⅛ 16⅜ 16⅝ 16⅞ 17⅛ 17⅜ 17⅝ 17⅞",
    ["12¾", "8¾", "8¼"]),
  row(44,
    "9⅞ 10 10⅛ 10¼ 10⅜ 10½ 10⅝ 10¾ 10⅞",
    "13⅜ 13½ 13⅝ 13¾ 13⅞ 14 14⅛ 14¼ 14⅜",
    "18⅝ 18⅞ 19 19¼ 19⅜ 19⅝ 19¾ 19⅞ 20⅛",
    "15¾ 16 16¼ 16½ 16¾ 17 17¼ 17½ 17¾",
    ["13", "9", "8½"]),
  row(45,
    "10 10⅛ 10¼ 10⅜ 10½ 10⅝ 10¾ 10⅞ 11",
    "13½ 13⅝ 13¾ 13⅞ 14 14⅛ 14¼ 14⅜ 14½",
    "18⅞ 19 19¼ 19⅜ 19⅝ 19¾ 19⅞ 20⅛ 20¼",
    "15½ 15¾ 16 16¼ 16½ 16¾ 17 17¼ 17½",
    ["13¼", "9¼", "8⅝"]),
  row(46,
    "10⅛ 10¼ 10⅜ 10½ 10⅝ 10¾ 10⅞ 11 11⅛",
    "13¾ 13⅞ 14 14⅛ 14¼ 14⅜ 14½ 14⅝ 14¾",
    "19 19¼ 19⅜ 19⅝ 19¾ 19⅞ 20⅛ 20¼ 20½",
    "15⅜ 15⅝ 15⅞ 16⅛ 16⅜ 16⅝ 16⅞ 17⅛ 17⅜",
    ["13½", "9½", "8⅞"]),
  row(47,
    "10¼ 10⅜ 10½ 10⅝ 10¾ 10⅞ 11 11⅛ 11¼",
    "13⅞ 14 14⅛ 14¼ 14⅜ 14½ 14⅝ 14¾ 14⅞",
    "19¼ 19⅜ 19⅝ 19¾ 20 20⅛ 20⅜ 20½ 20¾",
    "15⅛ 15⅜ 15⅝ 15⅞ 16⅛ 16⅜ 16⅝ 16⅞ 17⅛",
    ["13¾", "9¾", "9"]),
  row(48,
    // The book prints 10¾ at 5 ft 4 in; the run (⅛ in per inch of height) gives 10⅜.
    "10⅜ 10½ 10⅝ 10¾ 10⅞ 11 11⅛ 11¼ 11⅜",
    "14 14⅛ 14¼ 14⅜ 14½ 14⅝ 14¾ 14⅞ 15",
    "19⅜ 19⅝ 19¾ 19⅞ 20⅛ 20¼ 20½ 20⅝ 20⅞",
    "15 15¼ 15½ 15¾ 16 16¼ 16½ 16¾ 17",
    ["14", "10", "9⅛"]),
];

/** Cells corrected from the printed table, for the record. */
export const supremeProportionMisprints = [
  { breast: 41, height: 72, measure: "sleeveLength", printed: 18.875, corrected: 18.25 },
  { breast: 48, height: 64, measure: "depthOfScye", printed: 10.75, corrected: 10.375 },
] as const;

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** Bracketing indices and fraction for `value` in an ascending list, or undefined outside it. */
function bracket(values: readonly number[], value: number): [number, number, number] | undefined {
  if (value < values[0] || value > values[values.length - 1]) return undefined;
  for (let i = 0; i < values.length - 1; i += 1) {
    if (value <= values[i + 1]) return [i, i + 1, (value - values[i]) / (values[i + 1] - values[i])];
  }
  return [values.length - 1, values.length - 1, 0];
}

/**
 * The table's short measures for a breast and height in inches, read straight
 * off the table at its own sizes and interpolated linearly between them.
 * Undefined outside 32–48 breast or 5 ft 4 in to 6 ft; the waist is undefined
 * above 42 breast, where the book leaves it blank.
 */
export function supremeProportionateMeasures(breast: number, height: number): SupremeProportionateMeasures | undefined {
  const rows = bracket(supremeProportionTable.map((r) => r.breast), breast);
  const columns = bracket(supremeProportionHeights, height);
  if (!rows || !columns) return undefined;
  const [r0, r1, s] = rows;
  const [c0, c1, t] = columns;
  const lower = supremeProportionTable[r0];
  const upper = supremeProportionTable[r1];
  const cell = (key: "depthOfScye" | "strap" | "overShoulder" | "sleeveLength") =>
    lerp(lerp(lower[key][c0], lower[key][c1], t), lerp(upper[key][c0], upper[key][c1], t), s);
  const waist = lower.waist !== undefined && upper.waist !== undefined ? lerp(lower.waist, upper.waist, s) : undefined;
  return {
    depthOfScye: cell("depthOfScye"),
    strap: cell("strap"),
    overShoulder: cell("overShoulder"),
    sleeveLength: cell("sleeveLength"),
    blade: lerp(lower.blade, upper.blade, s),
    frontMeasure: lerp(lower.frontMeasure, upper.frontMeasure, s),
    widthOfBack: lerp(lower.widthOfBack, upper.widthOfBack, s),
    ...(waist !== undefined ? { waist } : {}),
  };
}
